import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import styled, { keyframes, css } from 'styled-components';
import roleApi from '../services/roleApi';

// --- Assets / Icons ---
const GlobeIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10" />
    <line x1="2" y1="12" x2="22" y2="12" />
    <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
  </svg>
);

const UserCheckIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
    <circle cx="8.5" cy="7" r="4" />
    <polyline points="17 11 19 13 23 9" />
  </svg>
);

const CalendarIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ opacity: 0.7 }}>
    <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
    <line x1="16" y1="2" x2="16" y2="6" />
    <line x1="8" y1="2" x2="8" y2="6" />
    <line x1="3" y1="10" x2="21" y2="10" />
  </svg>
);

const MapPinIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ opacity: 0.7 }}>
    <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
    <circle cx="12" cy="10" r="3" />
  </svg>
);


// --- Styled Components ---

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: translateY(0); }
`;

const Page = styled.div`
  position: relative;
  min-height: 100vh;
  padding: 110px 24px 40px; /* Top padding clears fixed navbar */
  background-color: #050505;
  color: #e5edff;
  overflow-x: hidden;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;

  /* Mesh Background */
  background-image: 
    radial-gradient(at 10% 20%, rgba(41, 151, 255, 0.1) 0px, transparent 40%),
    radial-gradient(at 90% 10%, rgba(165, 180, 252, 0.1) 0px, transparent 40%),
    radial-gradient(at 50% 90%, rgba(41, 151, 255, 0.05) 0px, transparent 50%);
  background-attachment: fixed;
`;

const ContentContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  animation: ${fadeIn} 0.6s cubic-bezier(0.2, 0.8, 0.2, 1);
`;

const HeroSection = styled.div`
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  margin-bottom: 4rem;
  padding-bottom: 2rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
  position: relative;

  &::after {
    content: '';
    position: absolute;
    bottom: -1px;
    left: 0;
    width: 140px;
    height: 1px;
    background: linear-gradient(90deg, #2997ff, transparent);
  }

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 1.5rem;
  }
`;

const TitleGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const Badge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  border-radius: 100px;
  font-size: 0.85rem;
  font-weight: 600;
  letter-spacing: 0.01em;
  background: ${props => props.type === 'PROMOTER'
    ? 'rgba(50, 215, 75, 0.15)'
    : 'rgba(41, 151, 255, 0.15)'};
  color: ${props => props.type === 'PROMOTER' ? '#32d74b' : '#2997ff'};
  border: 1px solid ${props => props.type === 'PROMOTER'
    ? 'rgba(50, 215, 75, 0.2)'
    : 'rgba(41, 151, 255, 0.2)'};
  width: fit-content;
  margin-bottom: 0.5rem;
`;

const PageTitle = styled.h1`
  font-size: 3rem;
  font-weight: 700;
  margin: 0;
  letter-spacing: -0.02em;
  background: linear-gradient(180deg, #fff 0%, #b0c4de 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  text-shadow: 0 0 30px rgba(255, 255, 255, 0.1);

  @media (max-width: 768px) {
    font-size: 2.2rem;
  }
`;

const Subtitle = styled.p`
  margin: 0;
  font-size: 1.1rem;
  color: rgba(255, 255, 255, 0.6);
  max-width: 500px;
  line-height: 1.5;
`;

const StatsGroup = styled.div`
  display: flex;
  gap: 1.5rem;
`;

const StatItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;

  span:first-child {
    font-size: 0.85rem;
    color: rgba(255, 255, 255, 0.5);
    text-transform: uppercase;
    letter-spacing: 0.05em;
  }

  span:last-child {
    font-size: 1.1rem;
    font-weight: 600;
    color: #fff;
  }
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
  gap: 1.5rem;
`;

const Card = styled.div`
  position: relative;
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.06);
  border-radius: 20px;
  padding: 1.5rem;
  display: flex;
  flex-direction: column;
  gap: 1rem;
  transition: all 0.3s cubic-bezier(0.2, 0.8, 0.2, 1);
  overflow: hidden;

  /* Glossy hover effect */
  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background: radial-gradient(800px circle at var(--mouse-x) var(--mouse-y), rgba(255, 255, 255, 0.06), transparent 40%);
    opacity: 0;
    transition: opacity 0.5s;
    pointer-events: none;
  }

  &:hover {
    background: rgba(255, 255, 255, 0.05);
    border-color: rgba(255, 255, 255, 0.1);
    transform: translateY(-4px);
    box-shadow: 0 10px 40px -10px rgba(0, 0, 0, 0.5);
  }
`;

const CardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
`;

const CategoryTag = styled.span`
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  padding: 4px 8px;
  border-radius: 6px;
  background: rgba(255, 255, 255, 0.1);
  color: rgba(255, 255, 255, 0.8);
`;

const EventTitle = styled.h3`
  font-size: 1.25rem;
  font-weight: 600;
  margin: 0;
  color: #fff;
  line-height: 1.3;
`;

const MoveInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
  margin-bottom: auto;
`;

const InfoRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.9rem;
  color: rgba(255, 255, 255, 0.6);
`;

const ActionButton = styled.button`
  margin-top: 1rem;
  width: 100%;
  padding: 0.8rem 1rem;
  border: none;
  border-radius: 12px;
  font-weight: 600;
  font-size: 0.95rem;
  cursor: pointer;
  transition: all 0.2s ease;
  position: relative;
  overflow: hidden;

  ${props => props.disabled ? css`
    background: rgba(255, 255, 255, 0.05);
    color: rgba(255, 255, 255, 0.3);
    cursor: not-allowed;
  ` : css`
    background: #fff;
    color: #000;
    box-shadow: 0 4px 12px rgba(255, 255, 255, 0.1);

    &:hover {
      transform: scale(1.02);
      box-shadow: 0 6px 16px rgba(255, 255, 255, 0.2);
    }
    
    &:active {
      transform: scale(0.98);
    }
  `}
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 4rem 2rem;
  background: rgba(255, 255, 255, 0.02);
  border: 1px dashed rgba(255, 255, 255, 0.1);
  border-radius: 20px;
  color: rgba(255, 255, 255, 0.5);

  h3 {
    margin: 0 0 0.5rem 0;
    color: #fff;
    font-size: 1.2rem;
  }
`;

// --- Configuration ---
const ROLE_LABELS = {
  promoter: {
    title: 'Become a Promoter',
    helper: 'Pick an event you want to promote. Your request goes to the event planner. Earn rewards for every ticket sold.',
    roleType: 'PROMOTER',
    icon: <GlobeIcon />,
  },
  staff: {
    title: 'Join Event Staff',
    helper: 'Choose an event you want to staff. The planner will review your request for shifts and responsibilities.',
    roleType: 'STAFF',
    icon: <UserCheckIcon />,
  },
};

const RoleRequestPage = () => {
  const { type } = useParams();
  const navigate = useNavigate();
  const config = ROLE_LABELS[type];

  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!config) {
      navigate('/not-found', { replace: true });
      return;
    }
    const fetchEvents = async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await roleApi.getEvents({ size: 50, sort: 'startTime,asc' });
        const list = res?.content ?? res ?? [];
        setEvents(list);
      } catch (err) {
        console.error('Failed to load events', err);
        setError(err?.message || 'Failed to load events');
      } finally {
        setLoading(false);
      }
    };
    fetchEvents();
  }, [type, navigate, config]);

  const handleSubmit = async (eventId) => {
    if (!config) return;
    setSubmitting(eventId);
    try {
      await roleApi.requestRole(eventId, config.roleType, null);
      toast.success('Request submitted for review');
    } catch (err) {
      console.error('Failed to submit request', err);
      toast.error(err?.message || 'Could not submit request');
    } finally {
      setSubmitting(null);
    }
  };

  if (!config) return null;

  return (
    <Page>
      <ContentContainer>
        <HeroSection>
          <TitleGroup>
            <Badge type={config.roleType}>
              {config.icon}
              {config.roleType}
            </Badge>
            <PageTitle>{config.title}</PageTitle>
            <Subtitle>{config.helper}</Subtitle>
          </TitleGroup>

          <StatsGroup>
            <StatItem>
              <span>Status</span>
              <span>Open</span>
            </StatItem>
            <StatItem>
              <span>Review Time</span>
              <span>~24h</span>
            </StatItem>
          </StatsGroup>
        </HeroSection>

        {loading && (
          <div style={{ textAlign: 'center', color: 'rgba(255,255,255,0.5)', padding: '4rem' }}>
            Looking for opportunities...
          </div>
        )}

        {error && (
          <div style={{ padding: '1rem', background: 'rgba(255,59,48,0.1)', color: '#ff3b30', borderRadius: '12px', marginBottom: '2rem' }}>
            {error}
          </div>
        )}

        {!loading && !error && (
          <>
            {events.length === 0 ? (
              <EmptyState>
                <h3>No Opportunities Available</h3>
                <p>Check back later for new events looking for {type}.</p>
              </EmptyState>
            ) : (
              <Grid>
                {events.map((event) => {
                  const start = event.startTime ? new Date(event.startTime).toLocaleString(undefined, {
                    month: 'short', day: 'numeric', hue: 'numeric', minute: '2-digit'
                  }) : 'Date TBA';

                  return (
                    <Card key={event.id}>
                      <CardHeader>
                        <CategoryTag>{event.category || 'Event'}</CategoryTag>
                      </CardHeader>

                      <EventTitle>{event.title}</EventTitle>

                      <MoveInfo>
                        <InfoRow>
                          <CalendarIcon />
                          {start}
                        </InfoRow>
                        {event.venue?.name && (
                          <InfoRow>
                            <MapPinIcon />
                            {event.venue.name}
                          </InfoRow>
                        )}
                      </MoveInfo>

                      <ActionButton
                        onClick={() => handleSubmit(event.id)}
                        disabled={!!submitting}
                      >
                        {submitting === event.id ? 'Sending...' : 'Request Access'}
                      </ActionButton>
                    </Card>
                  );
                })}
              </Grid>
            )}
          </>
        )}
      </ContentContainer>
    </Page>
  );
};

export default RoleRequestPage;
